import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  activeViewIdAtomFamily,
  controlsSF,
  entityIdSF,
  isTranslatableItem,
  Log,
  normalizeToArrayValue,
  profileScopeAtom,
  SimpleResult,
  tableFiltersSchemaAtomFamily,
  tableLayoutPropsSF,
  transformFilterStoreObjectToCriteria,
  ViewApi,
  viewDtosAtomFamily,
  type TError,
  type ViewDto,
  type ViewScopeEnum,
} from "@ryde/shared";

import { usePageContext } from "../contexts";
import { useDynamicFormsFetch } from "./use-dynamic-forms-fetch";
import { useDynamicFormsUtils } from "./use-dynamic-forms.utils";

export const useViewActions = () => {
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const profileScope = useRecoilValue(profileScopeAtom);

  const {
    columnsOrder,
    columnsSorting,
    columnsGrouping,
    columnsVisibility,
    columnsSizing,
  } = useRecoilValue(tableLayoutPropsSF(entity));
  const { getGridControlLabelById } = useDynamicFormsUtils();

  const entityId = useRecoilValue(entityIdSF(entity));

  const tableFiltersSchema = useRecoilValue(
    tableFiltersSchemaAtomFamily(entity),
  );
  const filtersSchema = useMemo(
    () => tableFiltersSchema || {},
    [tableFiltersSchema],
  );
  const controls = useRecoilValue(controlsSF(entity));
  const { fetchAndSetViews, initTableFilters } = useDynamicFormsFetch(entity);
  const viewDtos = useRecoilValue(viewDtosAtomFamily(entity));
  const setActiveViewId = useSetRecoilState(activeViewIdAtomFamily(entity));

  const updateViewsAndFilters = useEvent(async (): Promise<void> => {
    const viewsResult = await fetchAndSetViews();

    // Only re-resolve the filters from a list that actually came back: on failure the previous list is kept
    // as-is, and re-running the filter resolution against it would drop the filters the user is looking at.
    if (viewsResult.success) {
      initTableFilters();
    }
  });

  const generateDefaultViewName = useCallback(() => {
    const result = [];

    for (const filterId in filtersSchema) {
      if (!Object.hasOwn(filtersSchema, filterId)) {
        continue;
      }
      const values = filtersSchema[filterId];

      if (!values) {
        continue;
      }

      const label = getGridControlLabelById(+filterId);

      result.push(`${label}: ${normalizeToArrayValue(values).join(", ")}`);
    }

    return result.join(" | ");
  }, [filtersSchema, getGridControlLabelById]);

  const createViewHandler = useCallback(
    async ({
      name,
      scope,
      isDefault,
      createViewEntityId,
    }: {
      name: string;
      isDefault: boolean;
      scope: ViewScopeEnum;
      createViewEntityId: number;
    }): Promise<SimpleResult<ViewDto>> => {
      const filterObj = transformFilterStoreObjectToCriteria(
        filtersSchema,
        controls,
      );

      let viewResult: SimpleResult<ViewDto>;

      try {
        const createdView = await ViewApi.create({
          name: name || generateDefaultViewName(),
          isDefault,
          viewState: {
            filtersCriteria: filterObj, // TODO check usage of this prop
            filtersSchema: filtersSchema,
            columnsSorting: columnsSorting,
            columnsGrouping: columnsGrouping,
            columnsOrder: columnsOrder,
            columnsVisibility: columnsVisibility,
            columnsSizing,
          },
          scope,
          entityId: createViewEntityId,
          companyId: profileScope?.companyId,
        });

        // The view is already created on the server, so a failed views refresh (reported by
        // `fetchAndSetViews`) must not be reported as a failed creation.
        await fetchAndSetViews();

        if (createdView?.id) {
          Log.debug(`View created with id ${createdView.id}`, createdView);
          setActiveViewId(createdView.id);
          viewResult = new SimpleResult({ success: true, data: createdView });
          Log.successAndToast(t("view.VIEW_CREATED_SUCCESSFULLY"));
        } else {
          viewResult = new SimpleResult({ success: false });
        }
      } catch (error) {
        Log.errorAndToast(t("errors.errorCreatingView"), error);
        viewResult = new SimpleResult({
          success: false,
          error: error as TError,
        });
      }

      return viewResult;
    },
    [
      filtersSchema,
      controls,
      generateDefaultViewName,
      columnsSorting,
      columnsGrouping,
      columnsOrder,
      columnsVisibility,
      columnsSizing,
      profileScope?.companyId,
      fetchAndSetViews,
      t,
      setActiveViewId,
    ],
  );

  const updateViewCriteriaOnlyHandler = useCallback(
    async (viewId: number) => {
      if (!entityId) {
        Log.warnAndToast(t("errors.noEntityFound"));
        return;
      }

      const filterObj = transformFilterStoreObjectToCriteria(
        filtersSchema,
        controls,
      );

      try {
        const updatedView = await ViewApi.update(Number(viewId), {
          viewState: {
            filtersCriteria: filterObj,
            filtersSchema: filtersSchema,
            columnsSorting: columnsSorting,
            columnsGrouping: columnsGrouping,
            columnsOrder: columnsOrder,
            columnsVisibility: columnsVisibility,
            columnsSizing,
          },
          entityId,
        });
        // The update already reached the server, so a failed views refresh (reported by `fetchAndSetViews`)
        // must not hide the update result from the user.
        await updateViewsAndFilters();

        Log.successAndToast(t("view.VIEW_UPDATED_SUCCESSFULLY"), updatedView);
      } catch (error) {
        Log.errorAndToast(t("errors.errorUpdatingView"), error);
      }
    },
    [
      columnsGrouping,
      columnsOrder,
      columnsSizing,
      columnsSorting,
      columnsVisibility,
      controls,
      entityId,
      filtersSchema,
      updateViewsAndFilters,
      t,
    ],
  );
  const updateViewHandler = useCallback(
    async ({
      name,
      isDefault,
      createViewEntityId,
      viewId,
      scope,
    }: {
      name: string;
      isDefault: boolean;
      createViewEntityId: number;
      viewId: number;
      scope: ViewScopeEnum;
    }): Promise<SimpleResult<ViewDto>> => {
      let updateResult: SimpleResult<ViewDto>;

      try {
        const updatedView = await ViewApi.update(Number(viewId), {
          scope,
          isDefault,
          name,
          entityId: createViewEntityId,
        });
        // The update already reached the server, so a failed views refresh (reported by `fetchAndSetViews`)
        // must not be reported as a failed update.
        await updateViewsAndFilters();

        if (updatedView?.id) {
          updateResult = new SimpleResult({ success: true, data: updatedView });
          Log.successAndToast(
            t("view.VIEW_SCOPE_UPDATED_SUCCESSFULLY"),
            updatedView,
          );
        } else {
          updateResult = new SimpleResult({ success: false });
        }
      } catch (error) {
        updateResult = new SimpleResult({
          success: false,
          error: error as TError,
        });
        Log.errorAndToast(t("errors.errorUpdatingView"), error);
      }

      return updateResult;
    },
    [updateViewsAndFilters, t],
  );

  const deleteViewHandler = useCallback(
    async (viewId: number) => {
      try {
        const deleteResult = await ViewApi.remove(viewId);

        if (!deleteResult?.success) {
          if (isTranslatableItem(deleteResult?.error)) {
            const errorMessage = t(deleteResult?.error?.translationKey);
            Log.errorAndToast(t("errors.errorDeletingView"), errorMessage);
            return;
          }

          Log.errorAndToast(
            t("errors.errorDeletingView"),
            t("errors.unknownAPIError"),
          );
          return;
        }

        // The view is already gone on the server, so a failed views refresh (reported by `fetchAndSetViews`)
        // must not hide the deletion from the user.
        await fetchAndSetViews();

        Log.successAndToast(t("view.VIEW_DELETED_SUCCESSFULLY"));
      } catch (e) {
        Log.errorAndToast(t("errors.errorDeletingView"), e);
      }
    },
    [fetchAndSetViews, t],
  );

  return {
    createViewHandler,
    updateViewCriteriaOnlyHandler,
    updateViewHandler,
    generateDefaultViewName,
    deleteViewHandler,
    views: viewDtos,
  };
};
